import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Cookie, Settings, X } from "lucide-react";

const COOKIE_KEY = "cookie-consent";
const COOKIE_EXPIRY_DAYS = 365;

interface CookiePreferences {
  functional: boolean; // always true
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
}

function getStoredPreferences(): CookiePreferences | null {
  try {
    const stored = localStorage.getItem(COOKIE_KEY);
    if (!stored) return null;
    const prefs: CookiePreferences = JSON.parse(stored);
    // Check expiry
    const daysSince = (Date.now() - prefs.timestamp) / (1000 * 60 * 60 * 24);
    if (daysSince > COOKIE_EXPIRY_DAYS) {
      localStorage.removeItem(COOKIE_KEY);
      return null;
    }
    return prefs;
  } catch {
    return null;
  }
}

function savePreferences(prefs: CookiePreferences) {
  localStorage.setItem(COOKIE_KEY, JSON.stringify(prefs));
}

function applyConsentMode(prefs: CookiePreferences) {
  if (typeof window === "undefined" || !window.gtag) return;

  window.gtag("consent", "update", {
    ad_storage: prefs.marketing ? "granted" : "denied",
    analytics_storage: prefs.analytics ? "granted" : "denied",
    ad_user_data: prefs.marketing ? "granted" : "denied",
    ad_personalization: prefs.marketing ? "granted" : "denied",
    personalization_storage: prefs.marketing ? "granted" : "denied",
    functionality_storage: "granted", // always
    security_storage: "granted", // always
  });
}

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(true);

  useEffect(() => {
    const prefs = getStoredPreferences();
    if (!prefs) {
      // No consent stored — show banner after brief delay
      const timer = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(timer);
    }
    // Already consented — apply stored preferences
    applyConsentMode(prefs);
  }, []);

  const acceptAll = useCallback(() => {
    const prefs: CookiePreferences = {
      functional: true,
      analytics: true,
      marketing: true,
      timestamp: Date.now(),
    };
    savePreferences(prefs);
    applyConsentMode(prefs);
    setVisible(false);
    setShowPreferences(false);
  }, []);

  const saveCustomPreferences = useCallback(() => {
    const prefs: CookiePreferences = {
      functional: true,
      analytics,
      marketing,
      timestamp: Date.now(),
    };
    savePreferences(prefs);
    applyConsentMode(prefs);
    setVisible(false);
    setShowPreferences(false);
  }, [analytics, marketing]);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[9999] pointer-events-none">
      <div className="absolute bottom-0 left-0 right-0 pointer-events-auto">
        {/* Preferences panel */}
        {showPreferences && (
          <div className="mx-auto max-w-lg mb-2 bg-card border border-border rounded-xl shadow-2xl p-6 mx-4 sm:mx-auto animate-in slide-in-from-bottom-4 duration-300">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Cookie-voorkeuren
              </h3>
              <button onClick={() => setShowPreferences(false)} className="text-muted-foreground hover:text-foreground">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              {/* Functional */}
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div>
                  <p className="font-medium text-foreground text-sm">Functionele cookies</p>
                  <p className="text-xs text-muted-foreground">Noodzakelijk voor het functioneren van de site</p>
                </div>
                <Switch checked disabled className="opacity-70" />
              </div>

              {/* Analytics */}
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div>
                  <p className="font-medium text-foreground text-sm">Analytische cookies</p>
                  <p className="text-xs text-muted-foreground">Google Analytics & Tag Manager</p>
                </div>
                <Switch checked={analytics} onCheckedChange={setAnalytics} />
              </div>

              {/* Marketing */}
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div>
                  <p className="font-medium text-foreground text-sm">Marketing cookies</p>
                  <p className="text-xs text-muted-foreground">Voor gepersonaliseerde advertenties</p>
                </div>
                <Switch checked={marketing} onCheckedChange={setMarketing} />
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={saveCustomPreferences} className="flex-1 bg-accent hover:bg-accent/90 text-accent-foreground">
                Voorkeuren opslaan
              </Button>
              <Button onClick={acceptAll} variant="outline" className="flex-1">
                Alles accepteren
              </Button>
            </div>
          </div>
        )}

        {/* Main banner */}
        {!showPreferences && (
          <div className="mx-4 sm:mx-auto max-w-2xl mb-4 bg-card border border-border rounded-xl shadow-2xl p-5 animate-in slide-in-from-bottom-4 duration-300">
            <div className="flex items-start gap-4">
              <div className="bg-accent/20 p-2 rounded-lg shrink-0 mt-0.5">
                <Cookie className="h-5 w-5 text-accent" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground mb-1 font-medium">
                  Wij gebruiken cookies
                </p>
                <p className="text-xs text-muted-foreground mb-4">
                  We gebruiken cookies om de website te laten functioneren, het verkeer te analyseren en voor marketing. 
                  Lees meer in ons{" "}
                  <a href="/privacy-website" className="underline text-accent hover:text-accent/80">privacybeleid</a>.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={acceptAll}
                    size="sm"
                    className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold px-6"
                  >
                    OK, doorgaan
                  </Button>
                  <Button
                    onClick={() => setShowPreferences(true)}
                    size="sm"
                    variant="ghost"
                    className="text-muted-foreground hover:text-foreground font-normal"
                  >
                    Wijzig uw voorkeuren
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
